export interface ISize {
	height: number;
	width: number;
}

export interface ILogoOverlayGeometry {
	/** Padding around the logo, scaled with the image so the inset reads the same at any preview size. */
	padding: string;
	/** What to hand the logo element. One axis is 'auto' so the logo keeps its aspect ratio. */
	size: { height: string | number; width: string | number };
}

/** The inset the publisher burns in, in the image's own pixels. */
const LOGO_PADDING_VERTICAL = 4;
const LOGO_PADDING_HORIZONTAL = 16;

const hasArea = (size: ISize) => size.width > 0 && size.height > 0;

/**
 * Where the overlay lands on a preview.
 *
 * `logoSizePx` is measured against the image's own pixels, because that is what the publisher
 * applies it to — so a preview has to scale it by however much the image is shrunk to fit on
 * screen, or the logo shown is not the logo that publishes. The logo is never enlarged past its
 * intrinsic size, matching the publisher.
 *
 * Returns undefined until both the image and the logo have reported a size; there is no honest
 * preview before that.
 */
export function getLogoOverlayGeometry({
	imageIntrinsicSize,
	imageRenderedSize,
	logoIntrinsicSize,
	logoSizePx,
}: {
	imageIntrinsicSize: ISize;
	imageRenderedSize: ISize;
	logoIntrinsicSize: ISize;
	logoSizePx: number;
}): ILogoOverlayGeometry | undefined {
	if (!hasArea(imageIntrinsicSize) || !hasArea(logoIntrinsicSize)) {
		return undefined;
	}

	const widthScale = imageRenderedSize.width / imageIntrinsicSize.width;
	const heightScale = imageRenderedSize.height / imageIntrinsicSize.height;

	// A tall logo is sized by its height and a wide one by its width, so `logoSizePx` always caps the
	// logo's longer edge.
	const isTaller = logoIntrinsicSize.width < logoIntrinsicSize.height;
	return {
		padding: `${LOGO_PADDING_VERTICAL * heightScale}px ${LOGO_PADDING_HORIZONTAL * widthScale}px`,
		size: isTaller
			? { height: Math.min(logoSizePx, logoIntrinsicSize.height) * heightScale, width: 'auto' }
			: { height: 'auto', width: Math.min(logoSizePx, logoIntrinsicSize.width) * widthScale },
	};
}
